/*----------------------------------
- DOMAINING NEWS ARCHIVE
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect } from 'preact/hooks';
import Pagination from '@uniquedomains/components/ui/pagination';

import Icon from '@/client/components/ui/icon/Icon';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import NewsletterSubscribeForm from '@/client/pages/landing/components/newsletter/NewsletterSubscribeForm';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildDomainingNewsArchiveGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import {
    HERO_SECTION_CLASS,
    PAGE_CONTAINER_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import {
    DOMAINING_NEWS_FEED_PATH,
    DOMAINING_NEWS_INDEX_PATH,
    DOMAINING_NEWS_MARKDOWN_PATH,
    domainingNewsPagePath,
    type PublishedNewsEdition,
    type PublishedNewsEditionCard,
} from '@uniquedomains/components/contracts/news/public';
import type { UserPreferenceNewsTypeId } from '@uniquedomains/components/contracts/users/preferences';
import { getDomainingNewsCategoryShortLabel } from '@uniquedomains/components/news/presentation';

import { buildDomainingNewsArchivePath } from '../lib/presentation';
import DomainingNewsArchiveRow from './components/DomainingNewsArchiveRow';
import DomainingNewsCategoryFilters from './components/DomainingNewsCategoryFilters';
import DomainingNewsLeadEdition from './components/DomainingNewsLeadEdition';

import '../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainingNewsArchivePageProps = {
    categoryCode?: UserPreferenceNewsTypeId | null;
    editions: PublishedNewsEditionCard[];
    /** Full first edition, only present on the unfiltered first page. */
    leadEdition?: PublishedNewsEdition | null;
    navigation?: LandingHeaderDomainsNavigation;
    pageNumber: number;
    totalItems: number;
    totalPages: number;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The phone action bar scrolls to this capture. */
const SUBSCRIBE_ANCHOR_ID = 'subscribe';

/*----------------------------------
- HELPERS
----------------------------------*/

const buildVisiblePageNumbers = (currentPage: number, totalPages: number): number[] => {
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function DomainingNewsArchivePage({
    categoryCode = null,
    editions,
    leadEdition = null,
    navigation = EMPTY_LANDING_NAVIGATION,
    pageNumber,
    totalItems,
    totalPages,
}: DomainingNewsArchivePageProps) {
    const visiblePages = buildVisiblePageNumbers(pageNumber, totalPages);
    // The lead card already carries the first row, so it is not repeated below.
    const rows = leadEdition ? editions.slice(1) : editions;
    // A category filter is a facet of the same archive page, so the GEO context
    // points at the unfiltered canonical the head already declares.
    const geoContext = buildDomainingNewsArchiveGeoContext({
        canonicalPath: pageNumber > 1 ? domainingNewsPagePath(pageNumber) : DOMAINING_NEWS_INDEX_PATH,
        markdownMirrorPath: DOMAINING_NEWS_MARKDOWN_PATH,
        pageNumber,
    });

    useEffect(() => {
        track('page_view', {
            page: 'domaining_news_archive',
            path: window.location.pathname + window.location.search,
            archivePage: pageNumber,
        });
    }, [pageNumber]);

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation}
                    currentPath={DOMAINING_NEWS_INDEX_PATH}
                    trackingPage="domaining_news_archive"
                    mobileActions={{
                        secondary: {
                            label: 'RSS',
                            href: DOMAINING_NEWS_FEED_PATH,
                            external: true,
                            testId: 'domaining-news-mobile-rss',
                        },
                        primary: {
                            label: 'Get the Monday brief',
                            href: `#${SUBSCRIBE_ANCHOR_ID}`,
                            testId: 'domaining-news-mobile-subscribe',
                        },
                    }}
                />

                <main id="top">
                    {/*
                        One grid for the three opening blocks: a phone reads title, latest
                        edition, newsletter, while `lg` lifts the newsletter beside the title
                        and lets the edition card span the full measure underneath.
                    */}
                    <section
                        class={`${PAGE_CONTAINER_CLASS} ${HERO_SECTION_CLASS} grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-end lg:gap-x-16 lg:gap-y-12`}
                    >
                        <div class="lg:col-start-1 lg:row-start-1">
                            <h1 class="text-[48px] font-bold leading-[0.96] tracking-[-0.055em] text-[#1b1d1e] lg:text-[84px] lg:leading-[0.94] lg:tracking-[-0.06em]">
                                Domaining News
                            </h1>
                            <p class="mt-3.5 max-w-[620px] text-pretty text-base leading-[1.6] text-[#66696b] lg:mt-[22px] lg:text-lg">
                                Everything the domain industry reported that day, in one summary, with key takeaways, plain answers and a direct link to every source.
                            </p>
                        </div>

                        {leadEdition ? (
                            <DomainingNewsLeadEdition
                                edition={leadEdition}
                                class="lg:col-span-2 lg:row-start-2"
                            />
                        ) : null}

                        <div
                            id={SUBSCRIBE_ANCHOR_ID}
                            class="flex scroll-mt-24 flex-col gap-3 rounded-[22px] bg-[#1b1d1e] px-5 py-[22px] shadow-[0_18px_50px_rgba(27,29,30,0.14)] lg:col-start-2 lg:row-start-1 lg:gap-4 lg:rounded-[24px] lg:p-6 lg:shadow-none"
                        >
                            <p class="text-pretty text-[23px] font-bold leading-[1.15] tracking-[-0.035em] text-white lg:text-xl lg:leading-[1.2] lg:tracking-[-0.03em]">
                                Get the brief before the US wakes up
                            </p>
                            {/* Desktop reads the promise under the field, as the mockup does. */}
                            <p class="text-[13.5px] leading-[1.6] text-white/60 lg:order-last lg:text-xs lg:text-[#ffffff8c]">
                                One email every Monday, 07:00 UTC. One click to leave.
                            </p>
                            <NewsletterSubscribeForm
                                source="website_news_archive"
                                tone="dark"
                                size="lg"
                                showArrow
                                onSubscribed={() => track('newsletter_subscribe_submitted', { source: 'website_news_archive' })}
                            />
                            <a
                                href={DOMAINING_NEWS_FEED_PATH}
                                class="inline-flex items-center gap-2 text-xs text-white/55 transition hover:text-[#ffffffcc] lg:hidden"
                            >
                                <Icon name="rss" size={13} strokeWidth={2} />
                                Prefer RSS? Follow the feed instead.
                            </a>
                        </div>
                    </section>

                    <section
                        class={`${PAGE_CONTAINER_CLASS} pb-14 pt-11 lg:pb-24 lg:pt-24`}
                        aria-labelledby="news-editions-title"
                        data-testid="domaining-news-archive"
                    >
                        <div class="flex items-center justify-between gap-6">
                            <h2
                                id="news-editions-title"
                                class="text-2xl font-bold tracking-[-0.035em] text-[#1b1d1e] lg:text-4xl lg:tracking-[-0.04em]"
                            >
                                {categoryCode
                                    ? `${getDomainingNewsCategoryShortLabel(categoryCode)} editions`
                                    : 'Earlier editions'}
                            </h2>
                            <a
                                href={DOMAINING_NEWS_FEED_PATH}
                                class="hidden items-center gap-2 text-sm font-semibold text-[#66696b] transition hover:text-[#1b1d1e] lg:inline-flex"
                            >
                                <Icon name="rss" size={14} strokeWidth={2} />
                                RSS feed
                            </a>
                        </div>

                        <div class="mt-3.5 lg:mt-5">
                            <DomainingNewsCategoryFilters activeCategoryCode={categoryCode} />
                        </div>

                        {rows.length ? (
                            <div class="mt-3.5 border-t border-[rgba(27,29,30,0.1)] lg:mt-6">
                                {rows.map((edition) => (
                                    <DomainingNewsArchiveRow key={edition.dateKey} edition={edition} />
                                ))}
                            </div>
                        ) : leadEdition ? null : (
                            <div class="mt-6 rounded-[26px] border border-[rgba(27,29,30,0.07)] bg-white px-6 py-16 text-center">
                                <h3 class="text-xl font-semibold tracking-[-0.025em] text-[#1b1d1e]">
                                    {categoryCode
                                        ? 'No edition covers this category yet.'
                                        : 'No edition is published yet.'}
                                </h3>
                                <p class="mx-auto mt-2 max-w-xl text-sm leading-6 text-[#66696b]">
                                    {categoryCode ? (
                                        <>
                                            Clear the filter to read every published edition.{' '}
                                            <a
                                                href={buildDomainingNewsArchivePath(1, null)}
                                                class="font-semibold text-[#1b1d1e] underline decoration-[#c4c2b6] underline-offset-4"
                                            >
                                                See all editions
                                            </a>
                                        </>
                                    ) : (
                                        'The first brief will appear here after the collection and editorial checks complete.'
                                    )}
                                </p>
                            </div>
                        )}

                        {editions.length ? (
                            // The count sits on the left of the page chips, so the kit
                            // Pagination is used without its right-aligned trailing slot.
                            <div class="flex flex-wrap items-center justify-between gap-4 pt-[18px] lg:pt-6">
                                <span class="text-[12.5px] tabular-nums text-[#66696b] lg:text-[13.5px]">
                                    Showing {editions.length} of {totalItems}{' '}
                                    {totalItems === 1 ? 'edition' : 'editions'}
                                </span>
                                {totalPages > 1 ? (
                                    <Pagination
                                        ariaLabel="News archive pages"
                                        page={pageNumber}
                                        totalPages={totalPages}
                                        pages={visiblePages}
                                        buildHref={(page) => buildDomainingNewsArchivePath(page, categoryCode)}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </section>

                    <PageGeoBlock className="pb-4" geo={geoContext} surface="domaining_news_archive" />
                </main>

                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage="domaining_news_archive" />
            </div>
        </div>
    );
}
